import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import * as styles from './EnabledStatusBadge.module.css';

/**
 * EnabledStatusBadge component for displaying enabled/disabled status
 * Used for saved reports and scheduled reports across tables and modals.
 * Shows a colored dot followed by status text (Enabled/Disabled).
 *
 * @param {Object} props
 * @param {number|string} props.status - Status value (0 = Disabled, 1 = Enabled)
 * @param {'savedReports'|'scheduledReports'} props.namespace - Translation namespace for status text
 * @returns {JSX.Element} Status badge element with dot indicator
 */
function EnabledStatusBadge({ status, namespace }) {
  const { t } = useTranslation();

  // Normalize status to number for comparison
  const normalizedStatus = parseInt(status, 10);
  const isEnabled = normalizedStatus === 1;

  const statusText = isEnabled
    ? t(`${namespace}.status.enabled`)
    : t(`${namespace}.status.disabled`);

  const statusClass = isEnabled ? styles.enabled : styles.disabled;

  return (
    <span className={`${styles.badge} ${statusClass}`}>
      {statusText}
    </span>
  );
}

EnabledStatusBadge.propTypes = {
  status: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  namespace: PropTypes.oneOf(['savedReports', 'scheduledReports']),
};

EnabledStatusBadge.defaultProps = {
  namespace: 'savedReports',
};

export default React.memo(EnabledStatusBadge);
